/**
 * Keeps authentication consistent with the `address` environment selected
 * via the URL, for every routed page (Home, Findings, etc).
 *
 * The OIDC authority is resolved once, before the router mounts (see
 * `ACTIVE_AUTHORITY` in `constants/config.ts`), from the `address` query
 * param present on the initial page load. This hook detects the two ways
 * that can drift out of sync with the *current* URL/session and corrects
 * them, without ever silently sending a token to the wrong environment:
 *
 * 1. The user navigated client-side to a different `address` without a
 *    full page reload (so `AuthProvider` is still using the authority from
 *    the previous `address`). This requires a full reload so `index.tsx`
 *    re-derives the correct authority before any further auth call is
 *    made; a client-side `navigate()` cannot swap an already-constructed
 *    OIDC client's authority.
 * 2. A token is present whose issuer (`profile.iss`) does not match the
 *    authority for the current `address` (e.g. a token cached from a
 *    previous session/environment). Only the scoped OIDC user state is
 *    cleared (`auth.removeUser()`); nothing else in storage is touched.
 *
 * In both cases the full current query string (`debug`, `address`,
 * `country`, `location`, `elementType`, ...) is preserved across the
 * redirect to `/login`, and redirects are guarded against looping.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ACTIVE_AUTHORITY, getAuthority } from 'constants/config';

function normalizeAuthority(authority: string): string {
  return authority.replace(/\/$/, '');
}

export function useAuthEnvironmentGuard(): void {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const reloadTriggeredRef = useRef(false);

  const requestedAddress = searchParams.get('address');
  const expectedAuthority = getAuthority(requestedAddress);

  useEffect(() => {
    // Case 1: the environment requested by the current URL no longer
    // matches the authority this app instance authenticated against.
    // Fail closed: force a full reload rather than silently continuing
    // against the wrong (e.g. production) authority.
    if (expectedAuthority !== ACTIVE_AUTHORITY) {
      if (reloadTriggeredRef.current) {
        return;
      }
      reloadTriggeredRef.current = true;
      window.location.assign(`${window.location.pathname}${location.search}`);
      return;
    }

    if (auth.isLoading) {
      return;
    }

    // Case 2: a cached token exists but was issued by a different
    // authority than the one currently in effect. Clear only the scoped
    // OIDC user state and force re-authentication against the correct
    // environment instead of sending it to a mismatched API.
    const issuer = auth.user?.profile?.iss;
    if (auth.user && issuer && normalizeAuthority(issuer) !== normalizeAuthority(expectedAuthority)) {
      auth.removeUser().finally(() => {
        if (location.pathname !== '/login') {
          navigate(`/login${location.search}`, { replace: true });
        }
      });
      return;
    }

    // No valid session for the current environment: send the user to
    // login, preserving the full query string so the original request
    // (debug/address/country/location/elementType, ...) survives the
    // round trip.
    if (!auth.user && location.pathname !== '/login') {
      navigate(`/login${location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedAuthority, auth.user, auth.isLoading, location.pathname, location.search, navigate]);
}
